/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { focusBoxShadow, HFlow, Icon, Text, Theme, Tooltip, useTheme } from 'bold-ui'
import { turquoise } from 'bold-ui/lib/styles/colors'
import { Ellipsis } from 'components/Ellipsis'
import { format } from 'date-fns'
import { ReactComponent as AgendamentoCompartilhadoIcon } from 'images/agenda/calendar-add-filled.svg'
import { useMemo, useState } from 'react'

import { AcoesAgendamentoReserva } from '../acoes-agendamento/AcoesAgendamentoReserva'
import { CompartilharVideochamadaDetalhesAgendamentoCompartilhadoModal } from './CompartilharVideochamadaDetalhesAgendamentoCompartilhadoModal'
import { EventoAgendamentoProps } from './EventoAgendamento'
import { EventoAgendamentoPopperButton } from './EventoAgendamentoPopperButton'

const formatoHorario = 'HH:mm'

export function EventoAgendamentoReserva({
  event,
  eventHasOverlap,
  isAgendaOnlineAtiva,
  lotacaoAgendadaId,
}: EventoAgendamentoProps) {
  const theme = useTheme()
  const styles = useMemo(() => createStyles(theme, eventHasOverlap), [eventHasOverlap, theme])
  const motivoReserva = event.motivoReserva
  const detalheReserva = event.nomeProfissionalParticipante ? ' | Agendamento entre profissionais' : ' | Outros'

  const [isEnviarEmailModalOpen, setIsEnviarEmailOpen] = useState<boolean>(false)
  const [containerElement, setContainerElement] = useState<Element>()

  const emailDestinatario = useMemo(
    () =>
      event.lotacaoProfissionalAnfitriaoId === lotacaoAgendadaId
        ? event.emailProfissionalParticipante
        : event.emailProfissionalAnfitriao,
    [
      event.emailProfissionalAnfitriao,
      event.emailProfissionalParticipante,
      event.lotacaoProfissionalAnfitriaoId,
      lotacaoAgendadaId,
    ]
  )

  return (
    <div ref={setContainerElement} css={styles.container}>
      <CompartilharVideochamadaDetalhesAgendamentoCompartilhadoModal
        videochamadaUuid={event.videochamadaUuid}
        emailDestinatario={emailDestinatario}
        open={isEnviarEmailModalOpen}
        onClose={() => setIsEnviarEmailOpen(false)}
      />
      <EventoAgendamentoPopperButton
        isAgendaOnlineAtiva={isAgendaOnlineAtiva}
        event={event}
        closeOnBackdropClick={!isEnviarEmailModalOpen}
        style={styles.popperButton}
        onEnviarEmailButtonClick={() => setIsEnviarEmailOpen(true)}
      >
        {eventHasOverlap ? (
          <Ellipsis title={motivoReserva}>
            <Text fontWeight='bold' style={styles.motivo}>
              {motivoReserva}
            </Text>
          </Ellipsis>
        ) : (
          <HFlow style={styles.eventoSemOverlapWrapper}>
            {event.nomeProfissionalParticipante ? (
              <Tooltip text='Agendamento entre profissionais'>
                <AgendamentoCompartilhadoIcon css={styles.iconeAgendCompartilhado} />
              </Tooltip>
            ) : (
              <Icon style={styles.iconeBookmark} size={1} icon='bookmarkFilled' />
            )}
            <div css={styles.internalContainer}>
              <HFlow hSpacing={0} style={styles.motivoContainer}>
                <Ellipsis title={motivoReserva + ' | Outros'} style={styles.motivo}>
                  <Text fontWeight='bold'>
                    {event?.nomeProfissionalParticipante && event.lotacaoProfissionalAnfitriaoId === lotacaoAgendadaId
                      ? `Profissional ${event.nomeProfissionalParticipante}`
                      : event.nomeProfissionalAnfitriao
                      ? `Profissional ${event.nomeProfissionalAnfitriao}`
                      : motivoReserva}
                  </Text>
                  {(event.nomeProfissionalParticipante || event.isOutroMotivoReserva) && (
                    <Text style={styles.outros}>{detalheReserva}</Text>
                  )}
                </Ellipsis>
              </HFlow>
              <HFlow hSpacing={0} style={styles.observacoesContainer}>
                <Text fontWeight='bold'>
                  {format(event.start, formatoHorario)} até {format(event.end, formatoHorario)}
                </Text>
                {event.observacoes && (
                  <HFlow hSpacing={0} style={styles.observacoes}>
                    <Ellipsis title={event.observacoes}>
                      <Text fontWeight='bold'>&nbsp;|&nbsp;</Text>
                      <Text fontWeight='bold'>{'Observações:'}&nbsp;</Text>
                      <Text>{event.observacoes}</Text>
                    </Ellipsis>
                  </HFlow>
                )}
              </HFlow>
            </div>
          </HFlow>
        )}
      </EventoAgendamentoPopperButton>
      <AcoesAgendamentoReserva event={event} eventoElement={containerElement} />
    </div>
  )
}

const createStyles = (theme: Theme, eventHasOverlap: boolean) => ({
  container: css`
    height: calc(100% - 0.25rem);
    display: flex;
    width: 100%;
    background-color: ${turquoise.c90};
    border: 1px solid ${turquoise.c60};
    border-radius: 2px;
    margin: 0;
    min-height: 3rem;
    box-shadow: ${theme.shadows.outer[10]};
    &:focus-within {
      outline: none;
      box-shadow: ${focusBoxShadow(theme)};
    }
  `,

  internalContainer: css`
    padding: 0.25rem 0;
    align-items: flex-start;
    justify-content: center;
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    overflow: hidden;

    span {
      white-space: nowrap;
    }
  `,

  motivo: css`
    color: ${turquoise.c40};
  `,
  motivoContainer: css`
    max-width: 100%;
    display: flex;
    align-items: baseline;
    justify-content: flex-start;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  `,
  observacoesContainer: css`
    max-width: 100%;
    width: 100%;
    display: flex;
    align-items: baseline;
  `,
  observacoes: css`
    display: flex;
    align-items: baseline;
    justify-content: flex-start;
    text-overflow: ellipsis;
    overflow: hidden;
    white-space: nowrap;
  `,
  outros: css`
    margin-left: 0.25rem;
    color: ${turquoise.c40};
    white-space: nowrap;
  `,

  iconeBookmark: css`
    margin-top: 0.25rem;
    height: 1.5rem;
    margin-right: -0.4rem;
    color: ${turquoise.c40};
  `,
  iconeAgendCompartilhado: css`
    margin-top: 0.5rem;
    height: 1rem;
    width: 1rem;
    margin-right: -0.4rem;
    fill: ${turquoise.c40};
  `,
  popperButton: eventHasOverlap
    ? css`
        padding-left: 1.5rem;
        overflow: hidden;
        padding-right: 0.75rem;
      `
    : css`
        padding: 0.25rem 0 0.25rem 1rem;
        width: 95%;
      `,
  eventoSemOverlapWrapper: css`
    width: 100%;
    grid-template-columns: repeat(3, auto);
    justify-content: flex-start;
  `,
})
